import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { PublicPage, PageIntro, GREEN, GREEN_DEEP, GOLD, CREAM, INK } from "../../components/public/PublicChrome";
import { site, formatPlanPrice } from "../../content/siteData";

export function PlanCards({ compact = false }) {
  const { planes } = site.precios;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="pricing-plans">
      {planes.map((p) => {
        const price = formatPlanPrice(p);
        const featured = !!p.destacado;
        return (
          <div
            key={p.id}
            className="relative flex flex-col rounded-sm border p-6 md:p-7"
            style={{
              backgroundColor: featured ? GREEN : "#fff",
              borderColor: featured ? GREEN : `${GREEN}1f`,
              color: featured ? CREAM : INK,
            }}
            data-testid={`plan-${p.id}`}
          >
            {featured && (
              <span
                className="absolute -top-3 left-6 text-[10px] tracking-[0.2em] uppercase font-semibold px-2.5 py-1 rounded-sm"
                style={{ backgroundColor: GOLD, color: GREEN_DEEP }}
              >
                Más elegido
              </span>
            )}
            <h3 className="font-display text-2xl" style={{ color: featured ? "#fff" : GREEN }}>
              Plan {p.nombre}
            </h3>
            <p className="text-[13px] mt-1.5 min-h-[40px]" style={{ opacity: 0.75 }}>
              {p.descripcion}
            </p>
            <div className="mt-5 mb-5">
              <div className="font-display text-[34px] leading-none" style={{ color: featured ? GOLD : GREEN }}>
                {price.monto}
              </div>
              <div className="text-[12px] mt-1.5 uppercase tracking-[0.12em]" style={{ opacity: 0.65 }}>
                {price.sufijo}
              </div>
            </div>
            {!compact && (
              <ul className="space-y-2.5 text-[14px] flex-1">
                {p.incluye.map((f) => (
                  <li key={f} className="flex gap-2">
                    <Check className="h-4 w-4 shrink-0 mt-0.5" style={{ color: GOLD }} strokeWidth={2.25} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            )}
            <Link
              to="/#contacto"
              className="mt-6 inline-flex items-center justify-center gap-2 text-[14px] font-semibold px-5 py-3 rounded-sm transition-opacity hover:opacity-90"
              style={featured ? { backgroundColor: GOLD, color: GREEN_DEEP } : { backgroundColor: GREEN, color: CREAM }}
            >
              {p.aMedida ? "Solicitar cotización" : "Contratar plan"} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        );
      })}
    </div>
  );
}

export default function Precios() {
  return (
    <PublicPage
      title="Planes y precios"
      description="Planes de suscripción mensual del ERP contable Aequor y modalidades de cobro de los servicios profesionales."
    >
      <PageIntro eyebrow="Precios" title="Planes y precios">
        <p>
          La plataforma ERP se contrata por suscripción {site.precios.periodicidad}, sin permanencia mínima. Los
          servicios profesionales se cotizan según cada cliente.
        </p>
      </PageIntro>

      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-14 md:py-16">
        <h2 className="font-display text-3xl md:text-4xl mb-2" style={{ color: GREEN }}>
          Plataforma ERP contable
        </h2>
        <p className="text-[15px] mb-10" style={{ color: `${INK}99` }}>
          Suscripción {site.precios.periodicidad} en {site.precios.moneda}. Puedes cambiar de plan o cancelar cuando quieras.
        </p>
        <PlanCards />
        <p className="text-[13px] mt-6 leading-relaxed" style={{ color: `${INK}88` }}>
          {site.precios.notaImpuestos} Consulta las condiciones de cancelación en la{" "}
          <Link to="/reembolsos" className="underline" style={{ color: GREEN }}>
            Política de reembolsos y cancelación
          </Link>
          .
        </p>
      </section>

      <section className="bg-white border-y" style={{ borderColor: `${GREEN}14` }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-14 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <h2 className="font-display text-3xl md:text-4xl mb-3" style={{ color: GREEN }}>
              Servicios profesionales
            </h2>
            <p className="text-[15px] leading-relaxed" style={{ color: `${INK}aa` }}>
              Consultoría, asesoría, gestión, solución de problemas y auditoría contable y financiera. Estos servicios no
              tienen un precio fijo: el monto se acuerda por escrito con cada cliente según el alcance, el volumen de
              trabajo y la complejidad, antes de comenzar.
            </p>
            <Link
              to="/servicios"
              className="inline-flex items-center gap-2 mt-6 text-[14px] font-semibold"
              style={{ color: GOLD }}
            >
              Ver todos los servicios <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {site.servicios.modalidades.map((m) => (
              <div key={m.titulo} className="border rounded-sm p-5" style={{ borderColor: `${GREEN}1f`, backgroundColor: CREAM }}>
                <div className="font-display text-xl mb-1.5" style={{ color: GREEN }}>{m.titulo}</div>
                <p className="text-[13px] leading-relaxed" style={{ color: `${INK}aa` }}>{m.desc}</p>
                <div className="text-[11px] uppercase tracking-[0.15em] font-semibold mt-3" style={{ color: GOLD }}>
                  Según cotización
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PublicPage>
  );
}
